import _                      from 'underscore';
import { TooltipOptions }     from 'chart.js/dist/types';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { Chart }              from 'chart.js';

import { CorvalCostOccurrenceFee, CorvalCostOccurrenceFeeType, CorvalCostOccurrenceResource, CorvalProject, CorvalProjectActivity, Uuid } from '../types';

import { generateVisualizationDataColors, getArrColumnTotals, getCSSVar, maybeStaticVersionField, saveBlobAsFile, sum, sumByAttribute } from '../util';

import { Charting }                    from '../types/charting';
import { ITermDictionary }             from '../lang/LangTypes';
import { ResourcesService }            from './ResourcesService';
import { TimeService }                 from './TimeService';
import { ProjectService }              from './ProjectService';
import { LangService }                 from './LangService';
import { CostOccurrenceItemDataModel } from "@src/views/pages/projects/resource-planning/budgeting/by-category/models/CostOccurrenceItemDataModel";


export enum BudgetCostTypesByProjectCostTypes {
    ACTIVITY_COSTS = 'Activity Cost',
    HIRE_COSTS     = 'Hire Cost',
    CUSTOM_COSTS   = 'Custom Cost',
}

export class ChartingService {

    static generateVisualizationColors( dataItemCount: number ): string[]{

        const colorPalette    = generateVisualizationDataColors( true ),
              colorCount      = colorPalette.length,
              generatedColors = Array( dataItemCount ).fill( undefined );

        return generatedColors.map( ( value, index ) => {

            // Recycle colors if there are more items than colors
            let currentIndex = index % colorCount;

            return colorPalette[ currentIndex ];
        } );
    }

    static getDefaultChartJSTooltipFormatOptions(): _DeepPartialObject<TooltipOptions<any>>{

        return {
            backgroundColor:    'rgba(90, 40, 82, 0.9)', // var(--color-darkPurple) / 90% opacity
            titleColor:         '#FFF',
            titleFont:          { family: 'Lato' },
            titleAlign:         'left',
            titleSpacing:       4,
            titleMarginBottom:  6,
            bodyColor:          '#FFF',
            bodyFont:           { family: 'Lato' },
            bodyAlign:          'left',
            bodySpacing:        4,
            footerColor:        'rgba(255,255,255,0.8)',
            footerFont:         { family: 'Lato' },
            footerAlign:        'left',
            footerSpacing:      4,
            footerMarginTop:    6,
            padding:            20,
            caretPadding:       4,
            caretSize:          7,
            cornerRadius:       10,
            multiKeyBackground: 'rgba(0,0,0,0.8)',
            displayColors:      true,
            boxWidth:           10,
            boxHeight:          10,
            boxPadding:         5,
            usePointStyle:      false,
            borderColor:        'rgba(0,0,0,0)',
            borderWidth:        0,
        };
    }

    static fillCanvasBackgroundWithColor( canvas, color ){

        // Get the 2D drawing context from the provided canvas.
        const context = canvas.getContext( '2d' );

        // We're going to modify the context state, so it's
        // good practice to save the current state first.
        context.save();

        // Normally when you draw on a canvas, the new drawing
        // covers up any previous drawing it overlaps. This is
        // because the default `globalCompositeOperation` is
        // 'source-over'. By changing this to 'destination-over',
        // our new drawing goes behind the existing drawing. This
        // is desirable so we can fill the background, while leaving
        // the chart and any other existing drawing intact.
        // Learn more about `globalCompositeOperation` here:
        // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
        context.globalCompositeOperation = 'destination-over';

        // Fill in the background. We do this by drawing a rectangle
        // filling the entire canvas, using the provided color.
        context.fillStyle = color;
        context.fillRect( 0, 0, canvas.width, canvas.height );

        // Restore the original context state from `context.save()`
        context.restore();
    }

    static async saveChartAsImage( chartEntity: Chart, mimeType, fileName: string ){

        // Ensure a background color
        ChartingService.fillCanvasBackgroundWithColor( chartEntity.canvas, '#FFFFFF' );

        // Get the image data as a URL and use fetch to convert to a blob
        const chartImageData = chartEntity.toBase64Image( mimeType, 1 );
        const chartImage = await fetch( chartImageData );
        const chartImageBlob = await chartImage.blob();

        // Artifically create a link element to download the blob
        saveBlobAsFile( fileName, chartImageBlob, mimeType );

        // Revert background color
        ChartingService.fillCanvasBackgroundWithColor( chartEntity.canvas, 'transparent' );
    }

    static chartJSPluginLegendAdjustHeight( distance: number = 0 ){

        const plugin = {

            id: "adjust-legend-Height",

            beforeInit( chart: any ){

                // Get reference to the original fit function
                const originalFit = chart.legend.fit;

                // Override the fit function
                chart.legend.fit = function fit(){

                    // Call original function and bind scope in order to use `this` correctly inside it
                    originalFit.bind( chart.legend )();

                    // Change the attribute
                    this.height += distance;
                };
            }
        };

        return plugin;
    }

    static budgetCategoryDataByBusinessFunctionYearlyChartingData(
        data: CorvalCostOccurrenceFee[],
        filterMethod: null | ( ( co: CorvalCostOccurrenceFee ) => boolean ),
        termDictionary: ITermDictionary,
    ): Charting.Reports.BudgetCategoryDataByBusinessFunctionYearly.ChartingData{

        let timestamps                                                       = [],
            businessFunctionCosts: Record<string, CorvalCostOccurrenceFee[]> = {};

        // filter out items if necessary
        let filteredData = !filterMethod ? data : _.filter( data, filterMethod );

        // map timestamps
        filteredData.map( i => timestamps.push( TimeService.ISOtoMoment( i.costOccurrence ) ) );

        // group by business function
        businessFunctionCosts = _.groupBy( filteredData, co => {

            const itemData = new CostOccurrenceItemDataModel( co.itemData );

            return itemData.getBusinessFunction() ?? 'Unspecified';
        } );

        // sum totals for each business functions
        const businessFunctionCostTotals: Record<string, Record<number, number>> = _.mapObject( businessFunctionCosts, ( items, key ) => {

            let itemsByYear = _.groupBy( items, co => TimeService.ISOtoMoment( co.costOccurrence ).year() );

            return _.mapObject( itemsByYear, ( items ) => sumByAttribute( items, 'costAmount' ) );

        } );

        // upper / lower bounds of chart as years
        const years = TimeService.getYearArrayFromDates( timestamps );

        // dataset values
        const datasets = [] as Charting.Reports.BudgetCategoryDataByBusinessFunctionYearly.ChartingData['datasets'];

        _.mapObject( businessFunctionCostTotals, ( costByYear, businessFunction: Uuid ) => {

            const businessFunctionLabel = businessFunction.name,
                  businessFunctionColor = businessFunction.colorValue;

            datasets.push( {
                label:           LangService.getTermFromDictionary( businessFunctionLabel, termDictionary ),
                data:            years.map( year => costByYear[ year ] ?? 0 ),
                backgroundColor: getCSSVar( `--color-${ businessFunctionColor }` ),
                extendedData:    {
                    businessFunction
                }
            } );
        } );

        return {
            labels:   years,
            datasets: datasets,
        };
    }

    static budgetHiringDataByBusinessFunctionYearlyChartingData(
        data: CorvalCostOccurrenceResource[],
        filterMethod: null | ( ( co: CorvalCostOccurrenceResource ) => boolean ),
        termDictionary: ITermDictionary
    ): Charting.Reports.BudgetHiringDataByBusinessFunctionYearly.ChartingData{

        let timestamps                                                            = [],
            businessFunctionCosts: Record<string, CorvalCostOccurrenceResource[]> = {};

        // filter out items if necessary
        let filteredData = !filterMethod ? data : _.filter( data, filterMethod );

        // map timestamps
        filteredData.map( i => timestamps.push( TimeService.ISOtoMoment( i.resourceCostOccurrence ) ) );

        // group by business function
        businessFunctionCosts = _.groupBy( filteredData, co => co.hireTypeBusinessFunctionId );

        // sum totals for each business functions
        const businessFunctionCostTotals: Record<string, Record<number, number>> = _.mapObject( businessFunctionCosts, ( items, key ) => {

            let itemsByYear = _.groupBy( items, co => TimeService.ISOtoMoment( co.resourceCostOccurrence ).year() );

            return _.mapObject( itemsByYear, ( items ) => sumByAttribute( items, 'quarterlyCompensation' ) );

        } );

        // upper / lower bounds of chart as years
        const years = TimeService.getYearArrayFromDates( timestamps );

        // dataset values
        const datasets = [] as Charting.Reports.BudgetHiringDataByBusinessFunctionYearly.ChartingData['datasets'];

        _.mapObject( businessFunctionCostTotals, ( costByYear, businessFunction: Uuid ) => {

            const businessFunctionLabel = businessFunction.name,
                  businessFunctionColor = businessFunction.colorValue;

            datasets.push( {
                label:           LangService.getTermFromDictionary( businessFunctionLabel, termDictionary ),
                data:            years.map( year => costByYear[ year ] ?? 0 ),
                backgroundColor: getCSSVar( `--color-${ businessFunctionColor }` ),
                extendedData:    {
                    businessFunction
                }
            } );
        } );

        return {
            labels:   years,
            datasets: datasets,
        };
    }

    static budgetTotalsByCostTypeChartingData(
        feeData: CorvalCostOccurrenceFee[],
        hiringData: CorvalCostOccurrenceResource[],
        feeDataFilterMethod: null | ( ( co: CorvalCostOccurrenceFee ) => boolean ),
        hiringDataFilterMethod: null | ( ( co: CorvalCostOccurrenceResource ) => boolean ),
        termDictionary: ITermDictionary
    ): Charting.Reports.BudgetTotalsByCostType.ChartingData{

        // filter out items if necessary
        let filteredFeeData    = !feeDataFilterMethod ? feeData : _.filter( feeData, feeDataFilterMethod ),
            filteredHiringData = !hiringDataFilterMethod ? hiringData : _.filter( hiringData, hiringDataFilterMethod ),
            labels             = [];

        let {
                PROFESSIONALFEE,
                OPERATIONALCOST,
                ONGOINGCOST,
                CUSTOMBUDGETITEM,
            } = _.groupBy( filteredFeeData, co => co.costAmountType );

        let costTypeMap = new Map<string, { value: number, color: string }>( [
            [
                LangService.getTermFromDictionary( 'BUDGET_COSTS.PROFESSIONAL_FEE_COSTS.LABEL', termDictionary ),
                {
                    value: sumByAttribute( PROFESSIONALFEE, 'costAmount' ),
                    color: getCSSVar( `--color-blue` ),
                }
            ],
            [
                LangService.getTermFromDictionary( 'BUDGET_COSTS.OPERATIONAL_COSTS.LABEL', termDictionary ),
                {
                    value: sumByAttribute( OPERATIONALCOST, 'costAmount' ),
                    color: getCSSVar( `--color-green` ),
                }
            ],
            [
                LangService.getTermFromDictionary( 'BUDGET_COSTS.ONGOING_COSTS.LABEL', termDictionary ),
                {
                    value: sumByAttribute( ONGOINGCOST, 'costAmount' ),
                    color: getCSSVar( `--color-orange` ),
                }
            ],
            [
                LangService.getTermFromDictionary( 'BUDGET_COSTS.CUSTOM_DATA_CATEGORICAL_COSTS.LABEL', termDictionary ),
                {
                    value: sumByAttribute( CUSTOMBUDGETITEM, 'costAmount' ),
                    color: getCSSVar( `--color-pink` ),
                }
            ],
            [
                LangService.getTermFromDictionary( 'BUDGET_COSTS.HEADCOUNT_COST.LABEL', termDictionary ),
                {
                    value: sumByAttribute( filteredHiringData, 'quarterlyCompensation' ),
                    color: getCSSVar( `--color-teal` ),
                }
            ],
        ] );

        let datasets = [] as Charting.Reports.BudgetTotalsByCostType.ChartingData['datasets'];

        const primaryDataSet = {
            label:           'Total Budget Costs by Type',
            data:            Array() as Charting.Reports.BudgetTotalsByCostType.ChartingData['datasets'][0]['data'],
            backgroundColor: [],
            hoverOffset:     4
        };

        Array.from( costTypeMap ).map( ( [ name, definition ] ) => {
            labels.push( name );
            primaryDataSet.data.push( definition.value );
            primaryDataSet.backgroundColor.push( definition.color );
        } );

        datasets.push( primaryDataSet );

        return {
            labels,
            datasets,
        };
    }

    static budgetCostTypesByProjectChartingData(
        data: CorvalProject[],
        filterMethod: null | ( ( project: CorvalProject ) => boolean ),
        termDictionary: ITermDictionary,
        includedCostTypes: Array<BudgetCostTypesByProjectCostTypes>
    ): Charting.Reports.BudgetCostTypesByProject.ChartingData{

        interface ProjectByCostTypeDataObject {
            id: Uuid,
            name: string,
            hireCost: number,
            activityCost: number,
            customCost: number,
            totalCost: number
        }

        const allCostTypes = [
            BudgetCostTypesByProjectCostTypes.CUSTOM_COSTS,
            BudgetCostTypesByProjectCostTypes.HIRE_COSTS,
            BudgetCostTypesByProjectCostTypes.ACTIVITY_COSTS
        ];

        // Set default cost types if bad values are passed in
        // If there is an array, but no cost types, only include totals.
        includedCostTypes = _.isEmpty( includedCostTypes )
                            ? ( _.isArray( includedCostTypes ) && includedCostTypes.length === 0 )
                              ? []
                              : allCostTypes
                            : includedCostTypes;

        // Method to insepct if a particular cost type is included
        const isCostTypeIncluded = ( costType: BudgetCostTypesByProjectCostTypes ) => {

            return includedCostTypes.includes( costType );
        };

        const projectData          = new Map<Uuid, ProjectByCostTypeDataObject>( [] ),
              includeActivityCosts = isCostTypeIncluded( BudgetCostTypesByProjectCostTypes.ACTIVITY_COSTS ),
              includeHireCosts     = isCostTypeIncluded( BudgetCostTypesByProjectCostTypes.HIRE_COSTS ),
              includeCustomCosts   = isCostTypeIncluded( BudgetCostTypesByProjectCostTypes.CUSTOM_COSTS );

        const onlyTotalCostUsed = ( !includeActivityCosts && !includeHireCosts && !includeCustomCosts );

        // const singleCostTypeUsed = ( includedCostTypes.length === 1 );

        // Craft chart labels based on included cost types
        const costTypes = _.compact( [
            includeActivityCosts ? 'Activity Costs' : null,
            includeHireCosts ? LangService.getTermFromDictionary( 'BUDGET_COSTS.HEADCOUNT_COST.LABEL', termDictionary ) : null,
            includeCustomCosts ? LangService.getTermFromDictionary( 'BUDGET_COSTS.CUSTOM_DATA_CATEGORICAL_COSTS.LABEL', termDictionary ) : null,
            'Total Costs'
        ] );

        // Aggregate data costs by project
        data.map( ( project ) => {

            // Separate out custom costs and activity costs
            let [ customCostOccurrences, activityOccurrences ] = _.partition( project.budget.feeBasedCostOccurrences, ( co ) => co.costAmountType === CorvalCostOccurrenceFeeType.Custombudgetitem );

            // Calculate cost totals
            let activityCost = sumByAttribute( activityOccurrences, 'costAmount', 0 );
            let customCost = sumByAttribute( customCostOccurrences, 'costAmount', 0 );
            let hireCost = sumByAttribute( project.budget.resourceCostOccurrences, 'quarterlyCompensation', 0 );

            // Only add up cost stypes requested
            let totalCost = sum( _.compact( [
                includeActivityCosts ? activityCost : null,
                includeCustomCosts ? customCost : null,
                includeHireCosts ? hireCost : null,
            ] ) );

            // If only total cost exist, use the sum total of all costs
            if( onlyTotalCostUsed ){
                totalCost = sum( [ activityCost, customCost, hireCost, ] );
            }

            // Add project data to map
            projectData.set( project.id, {
                id:   project.id,
                name: project.name,
                activityCost,
                customCost,
                hireCost,
                totalCost
            } );

        } );

        // generate colors for each project
        const dataSetColors = ChartingService.generateVisualizationColors( data.length );

        // generate datasets
        const datasets = Array.from( projectData ).map( ( [ key, data ]: [ key: Uuid, data: ProjectByCostTypeDataObject ], index ) => {

            // Filter out data that is not included
            let preparedData = [
                includeActivityCosts ? data.activityCost : null,
                includeHireCosts ? data.hireCost : null,
                includeCustomCosts ? data.customCost : null,
                data.totalCost
            ].filter( cost => ( cost !== null ) );

            const hasMinLength = preparedData.every( ( cost ) => cost > 0 );

            // Return the dataset with the specifed color and only the cost types requested
            return {
                label:           data.name,
                data:            preparedData,
                backgroundColor: dataSetColors[ index ],
                minBarLength:    hasMinLength ? 6 : 0,
            };

        } ) as Charting.Reports.BudgetCostTypesByProject.ChartingData['datasets'];

        // Return the chart data
        return {
            labels: costTypes,
            datasets
        };
    }

    static activityCostByBusinessFunctionYearlyByProjectChartingData(
        data: CorvalProject[],
        filterMethod: null | ( ( co: CorvalCostOccurrenceFee ) => boolean ),
        termDictionary: ITermDictionary,
        includedBusinessFunctions: Uuid[],
        compileBusinessFunctions: boolean = false,
    ): Charting.Reports.ActivityCostByBusinessFunctionYearlyByProject.ChartingData{

        const projectDataSets      = [],
              allBusinessFunctions = ProjectService.getBusinessFunctionsFromProjects( data );

        const filterBusinessFunctions =
                  _.isArray( includedBusinessFunctions ) &&
                  ( includedBusinessFunctions.length !== 0 ) &&
                  ( includedBusinessFunctions.length !== allBusinessFunctions.length );

        // Full array of years
        const fullYearSet = _.chain( data )
                             .pluck( 'budget' ).flatten()
                             .pluck( 'feeBasedCostOccurrences' ).flatten()
                             .pluck( 'costOccurrence' ).flatten()
                             .map( date => TimeService.ISOtoMoment( date ).year() )
                             .uniq()
                             .sort()
                             .value();

        const dataSetColors = ChartingService.generateVisualizationColors( data.length );

        data.map( ( project, projectIndex ) => {

            let feeBasedCostOccurrences = project.budget.feeBasedCostOccurrences;

            let filteredFeeBasedCostOccurrences: CorvalCostOccurrenceFee[] = filterMethod ? feeBasedCostOccurrences.filter( filterMethod ) : feeBasedCostOccurrences;

            let projectChartData = {
                labels:   fullYearSet,
                datasets: [],
            };

            const commonDatasetProps = {
                stack: `${ project.id }`,
            };

            const commonDatasetExtendedProps = {
                projectName:  project.name,
                projectId:    project.id,
                projectColor: dataSetColors[ projectIndex ]
            };

            // Filter out business functions
            if( filterBusinessFunctions ){

                filteredFeeBasedCostOccurrences = filteredFeeBasedCostOccurrences.filter( co => {

                    return includedBusinessFunctions.includes( ( co.itemData as CorvalProjectActivity ).activity.businessFunctionId );

                } );
            }

            // Datasets by business function for each year
            const generateFullYearSetObject = () => _.object( fullYearSet, Array( fullYearSet.length ).fill( 0 ) );
            const groupedByBusinessFunction       = _.groupBy( filteredFeeBasedCostOccurrences, co => ( co.itemData as CorvalProjectActivity ).activity.businessFunctionId ),
                  groupedByBusinessFunctionByYear = _.mapObject( groupedByBusinessFunction, ( coGroup ) => {
                      return _.groupBy( coGroup, co => TimeService.ISOtoMoment( co.costOccurrence ).year() );
                  } );

            // Total count of activities by business function for each year
            const groupedByBusinessFunctionByYearTotals = _.mapObject( groupedByBusinessFunctionByYear, ( yearSet ) => {

                return _.chain( generateFullYearSetObject() )
                        .mapObject( ( sum, year ) => {
                            return ( sum + yearSet[ year ] ? sumByAttribute( yearSet[ year ], 'costAmount' ) : 0 );
                        } )
                        .values()
                        .value();
            } );

            // Total count of activities for each year
            const compiledYearlyCountTotals = getArrColumnTotals( _.values( groupedByBusinessFunctionByYearTotals ) );

            // Dataset per business function
            /*let colorStep = 25,
             colorTint = -colorStep;

             const businessFunctionDatasetColors = _.mapObject( groupedByBusinessFunctionByYear, ( yearlyTotals, businessFunction ) => {

             let color        = dataSetColors[ projectIndex ],
             isBright     = !isColorDark( color ),
             colorTintAmt = colorTint + colorStep;

             colorTint = colorTintAmt;

             return adjustBrightness( dataSetColors[ projectIndex ], colorTintAmt )
             } );*/

            const businessFunctionDatasets = _.mapObject( groupedByBusinessFunctionByYear, ( yearlyTotals, businessFunctionId ) => {

                const businessFunction = _.findWhere( allBusinessFunctions, { id: businessFunctionId } );

                return {
                    ...commonDatasetProps,
                    label:           businessFunction.name,
                    data:            groupedByBusinessFunctionByYearTotals[ businessFunctionId ],
                    backgroundColor: businessFunction.colorValue,
                    extendedData:    {
                        ...commonDatasetExtendedProps,
                        businessFunction:   businessFunction,
                        businessFunctionId: businessFunctionId,
                    }
                };
            } );

            projectChartData.datasets = _.values( businessFunctionDatasets );

            const hasProjectData = projectChartData.datasets.length;

            // Compact data?
            if( compileBusinessFunctions && hasProjectData ){

                projectChartData.datasets = [
                    {
                        ...commonDatasetProps,
                        label:           project.name,
                        data:            compiledYearlyCountTotals,
                        backgroundColor: dataSetColors[ projectIndex ],
                        extendedData:    {
                            ...commonDatasetExtendedProps,
                            businessFunction:          null,
                            businessFunctionBreakdown: businessFunctionDatasets,
                        }
                    }
                ];

            }

            // Single Project Data
            if( hasProjectData ){

                projectDataSets.push( projectChartData );
            }

        } );

        // All Project Data
        return {
            labels:   fullYearSet,
            datasets: _.chain( projectDataSets ).pluck( 'datasets' ).flatten().value(),
        };
    }

    static activityCountByBusinessFunctionYearlyByProjectChartingData(
        data: CorvalProject[],
        filterMethod: null | ( ( co: CorvalProjectActivity ) => boolean ),
        termDictionary: ITermDictionary,
        includedBusinessFunctions: Uuid[],
        compileBusinessFunctions: boolean = false,
    ): Charting.Reports.ActivityCountByBusinessFunctionYearlyByProject.ChartingData{

        const projectDataSets      = [],
              allBusinessFunctions = ProjectService.getBusinessFunctionsFromProjects( data );

        const filterBusinessFunctions =
                  _.isArray( includedBusinessFunctions ) &&
                  ( includedBusinessFunctions.length !== 0 ) &&
                  ( includedBusinessFunctions.length !== allBusinessFunctions.length );

        // Full array of years
        const fullYearSet = _.chain( data )
                             .pluck( 'projectActivities' ).flatten()
                             .pluck( 'startTimeUtc' ).flatten()
                             .map( date => TimeService.ISOtoMoment( date ).year() )
                             .uniq()
                             .sort()
                             .value();

        const dataSetColors = ChartingService.generateVisualizationColors( data.length );

        data.map( ( project, projectIndex ) => {

            const projectActivitiesField = maybeStaticVersionField( 'projectActivities', !project.isCorvalManaged );

            const projectActivities = project[ projectActivitiesField ];

            let filteredProjectActivities: CorvalProjectActivity[] = filterMethod ? projectActivities.filter( filterMethod ) : projectActivities;

            let projectChartData = {
                labels:   fullYearSet,
                datasets: [],
            };

            const commonDatasetProps = {
                stack: `${ project.id }`,
            };

            const commonDatasetExtendedProps = {
                projectName:  project.name,
                projectId:    project.id,
                projectColor: dataSetColors[ projectIndex ]
            };

            // Filter out business functions
            if( filterBusinessFunctions ){

                filteredProjectActivities = filteredProjectActivities.filter( projectActivity => {

                    return includedBusinessFunctions.includes( projectActivity.activity.businessFunctionId );

                } );
            }

            // Datasets by business function for each year
            const generateFullYearSetObject = () => _.object( fullYearSet, Array( fullYearSet.length ).fill( 0 ) );
            const groupedByBusinessFunction       = _.groupBy( filteredProjectActivities, pa => pa.activity.businessFunctionId ),
                  groupedByBusinessFunctionByYear = _.mapObject( groupedByBusinessFunction, ( projectActivities ) => {
                      return _.groupBy( projectActivities, pa => TimeService.ISOtoMoment( pa.startTimeUtc ).year() );
                  } );

            // Total count of activities by business function for each year
            const groupedByBusinessFunctionByYearTotals = _.mapObject( groupedByBusinessFunctionByYear, ( yearSet ) => {

                return _.chain( generateFullYearSetObject() )
                        .mapObject( ( sum, year ) => ( sum + yearSet[ year ] ? yearSet[ year ].length : 0 ) )
                        .values()
                        .value();
            } );

            // Total count of activities for each year
            const compiledYearlyCountTotals = getArrColumnTotals( _.values( groupedByBusinessFunctionByYearTotals ) );

            // Dataset per business function
            const businessFunctionDatasets = _.mapObject( groupedByBusinessFunctionByYear, ( yearlyTotals, businessFunctionId ) => {


                const businessFunction = _.findWhere( allBusinessFunctions, { id: businessFunctionId } );

                return {
                    ...commonDatasetProps,
                    label:           businessFunction.name,
                    data:            groupedByBusinessFunctionByYearTotals[ businessFunctionId ],
                    backgroundColor: businessFunction.colorValue,
                    extendedData:    {
                        ...commonDatasetExtendedProps,
                        businessFunctionId: businessFunctionId,
                        businessFunction:   businessFunction,
                    }
                };
            } );

            projectChartData.datasets = _.values( businessFunctionDatasets );

            const hasProjectData = projectChartData.datasets.length;

            // Compact data?
            if( compileBusinessFunctions && hasProjectData ){

                projectChartData.datasets = [
                    {
                        ...commonDatasetProps,
                        label:           project.name,
                        data:            compiledYearlyCountTotals,
                        backgroundColor: dataSetColors[ projectIndex ],
                        extendedData:    {
                            ...commonDatasetExtendedProps,
                            businessFunction:          null,
                            businessFunctionBreakdown: businessFunctionDatasets,
                        }
                    }
                ];

            }

            // Single Project Data
            if( hasProjectData ){

                projectDataSets.push( projectChartData );
            }

        } );

        // All Project Data
        return {
            labels:   fullYearSet,
            datasets: _.chain( projectDataSets ).pluck( 'datasets' ).flatten().value(),
        };
    }

    static hiringCostByBusinessFunctionYearlyByProjectChartingData(
        data: CorvalProject[],
        filterMethod: null | ( ( co: CorvalCostOccurrenceResource ) => boolean ),
        termDictionary: ITermDictionary,
        includedBusinessFunctions: Uuid[],
        compileBusinessFunctions: boolean = false,
    ): Charting.Reports.HiringCostByBusinessFunctionYearlyByProject.ChartingData{

        const projectDataSets      = [],
              allBusinessFunctions = ProjectService.getBusinessFunctionsFromProjects( data );

        const filterBusinessFunctions =
                  _.isArray( includedBusinessFunctions ) &&
                  ( includedBusinessFunctions.length !== 0 ) &&
                  ( includedBusinessFunctions.length !== allBusinessFunctions.length );

        // Full array of years
        const fullYearSet = _.chain( data )
                             .pluck( 'budget' ).flatten()
                             .pluck( 'resourceCostOccurrences' ).flatten()
                             .pluck( 'resourceCostOccurrence' ).flatten()
                             .map( date => TimeService.ISOtoMoment( date ).year() )
                             .uniq()
                             .sort()
                             .value();

        const dataSetColors = ChartingService.generateVisualizationColors( data.length );

        data.map( ( project, projectIndex ) => {

            let resourceCostOccurrences = project.budget.resourceCostOccurrences;

            let filteredFeeBasedCostOccurrences: CorvalCostOccurrenceResource[] = filterMethod ? resourceCostOccurrences.filter( filterMethod ) : resourceCostOccurrences;

            let projectChartData = {
                labels:   fullYearSet,
                datasets: [],
            };

            const commonDatasetProps = {
                stack: `${ project.id }`,
            };

            const commonDatasetExtendedProps = {
                projectName:  project.name,
                projectId:    project.id,
                projectColor: dataSetColors[ projectIndex ]
            };

            // Filter out business functions
            if( filterBusinessFunctions ){

                filteredFeeBasedCostOccurrences = filteredFeeBasedCostOccurrences.filter( co => {

                    return includedBusinessFunctions.includes( co.hireTypeBusinessFunctionId );

                } );
            }

            // Datasets by business function for each year
            const generateFullYearSetObject = () => _.object( fullYearSet, Array( fullYearSet.length ).fill( 0 ) );
            const groupedByBusinessFunction       = _.groupBy( filteredFeeBasedCostOccurrences, co => ( co.hireTypeBusinessFunctionId ) ),
                  groupedByBusinessFunctionByYear = _.mapObject( groupedByBusinessFunction, ( coGroup ) => {
                      return _.groupBy( coGroup, co => TimeService.ISOtoMoment( co.resourceCostOccurrence ).year() );
                  } );

            // Total count of activities by business function for each year
            const groupedByBusinessFunctionByYearTotals = _.mapObject( groupedByBusinessFunctionByYear, ( yearSet ) => {

                return _.chain( generateFullYearSetObject() )
                        .mapObject( ( sum, year ) => {
                            return ( sum + yearSet[ year ] ? sumByAttribute( yearSet[ year ], 'quarterlyCompensation' ) : 0 );
                        } )
                        .values()
                        .value();
            } );

            // Total count of activities for each year
            const compiledYearlyCountTotals = getArrColumnTotals( _.values( groupedByBusinessFunctionByYearTotals ) );

            const businessFunctionDatasets = _.mapObject( groupedByBusinessFunctionByYear, ( yearlyTotals, businessFunctionId ) => {

                const businessFunction = _.findWhere( allBusinessFunctions, { id: businessFunctionId } );

                return {
                    ...commonDatasetProps,
                    label:           businessFunction.name,
                    data:            groupedByBusinessFunctionByYearTotals[ businessFunctionId ],
                    backgroundColor: businessFunction.colorValue,
                    extendedData:    {
                        ...commonDatasetExtendedProps,
                        businessFunctionId: businessFunctionId,
                        businessFunction:   businessFunction,
                    }
                };
            } );

            projectChartData.datasets = _.values( businessFunctionDatasets );

            const hasProjectData = projectChartData.datasets.length;

            // Compact data?
            if( compileBusinessFunctions && hasProjectData ){

                projectChartData.datasets = [
                    {
                        ...commonDatasetProps,
                        label:           project.name,
                        data:            compiledYearlyCountTotals,
                        backgroundColor: dataSetColors[ projectIndex ],
                        extendedData:    {
                            ...commonDatasetExtendedProps,
                            businessFunction:          null,
                            businessFunctionBreakdown: businessFunctionDatasets,
                        }
                    }
                ];

            }

            // Single Project Data
            if( hasProjectData ){

                projectDataSets.push( projectChartData );
            }

        } );

        // All Project Data
        return {
            labels:   fullYearSet,
            datasets: _.chain( projectDataSets ).pluck( 'datasets' ).flatten().value(),
        };
    }

    static hiringCountByBusinessFunctionYearlyByProjectChartingData(
        data: CorvalProject[],
        filterMethod: null | ( ( co: CorvalCostOccurrenceResource ) => boolean ),
        termDictionary: ITermDictionary,
        includedBusinessFunctions: Uuid[],
        compileBusinessFunctions: boolean = false,
    ): Charting.Reports.HiringCountByBusinessFunctionYearlyByProject.ChartingData{

        const projectDataSets      = [],
              allBusinessFunctions = ProjectService.getBusinessFunctionsFromProjects( data );

        const filterBusinessFunctions =
                  _.isArray( includedBusinessFunctions ) &&
                  ( includedBusinessFunctions.length !== 0 ) &&
                  ( includedBusinessFunctions.length !== allBusinessFunctions.length );

        // Full array of years
        const fullYearSet = _.chain( data )
                             .pluck( 'budget' ).flatten()
                             .pluck( 'resourceCostOccurrences' ).flatten()
                             .pluck( 'resourceCostOccurrence' ).flatten()
                             .map( date => TimeService.ISOtoMoment( date ).year() )
                             .uniq()
                             .sort()
                             .value();

        const dataSetColors = ChartingService.generateVisualizationColors( data.length );

        data.map( ( project, projectIndex ) => {

            const resourceCostOccurrences = project.budget.resourceCostOccurrences;

            let filteredResourceCostOccurrences: CorvalCostOccurrenceResource[] = filterMethod ? resourceCostOccurrences.filter( filterMethod ) : resourceCostOccurrences;

            let projectChartData = {
                labels:   fullYearSet,
                datasets: [],
            };

            const commonDatasetProps = {
                stack: `${ project.id }`,
            };

            const commonDatasetExtendedProps = {
                projectName:  project.name,
                projectId:    project.id,
                projectColor: dataSetColors[ projectIndex ]
            };

            // Filter out business functions
            if( filterBusinessFunctions ){

                filteredResourceCostOccurrences = filteredResourceCostOccurrences.filter( co => {

                    return includedBusinessFunctions.includes( co.hireTypeBusinessFunctionId );

                } );
            }

            // Organize / count the initial resource cost occurrences
            const initialResourceCostOccurrenceCounts = ResourcesService.getHiringCountsByYearFromCostOccurrences( filteredResourceCostOccurrences );

            // Update the pool to just those that are included in the initial resource cost occurrences
            filteredResourceCostOccurrences = _.chain( initialResourceCostOccurrenceCounts ).values().pluck( 'items' ).flatten().value();

            // Datasets by business function for each year
            const generateFullYearSetObject = () => _.object( fullYearSet, Array( fullYearSet.length ).fill( 0 ) );
            const groupedByBusinessFunction       = _.groupBy( filteredResourceCostOccurrences, co => co.hireTypeBusinessFunctionId ),
                  groupedByBusinessFunctionByYear = _.mapObject( groupedByBusinessFunction, ( coSet ) => {
                      return _.groupBy( coSet, co => TimeService.ISOtoMoment( co.resourceCostOccurrence ).year() );
                  } );

            // Total count of activities by business function for each year
            const groupedByBusinessFunctionByYearTotals = _.mapObject( groupedByBusinessFunctionByYear, ( yearSet ) => {

                // Count all headcounts for each occurrence within the year
                return _.chain( generateFullYearSetObject() )
                        .mapObject( ( aggregate, year ) => {

                            return (
                                aggregate + yearSet[ year ]
                                ? sum( yearSet[ year ].map( co => co.projectHire.headcount ) )
                                : 0
                            );
                        } )
                        .values()
                        .value();
            } );

            // Total count of activities for each year
            const compiledYearlyCountTotals = getArrColumnTotals( _.values( groupedByBusinessFunctionByYearTotals ) );

            // Dataset per business function
            const businessFunctionDatasets = _.mapObject( groupedByBusinessFunctionByYear, ( yearlyTotals, businessFunctionId ) => {

                const businessFunction = _.findWhere( allBusinessFunctions, { id: businessFunctionId } );

                return {
                    ...commonDatasetProps,
                    label:           businessFunction.name,
                    data:            groupedByBusinessFunctionByYearTotals[ businessFunctionId ],
                    backgroundColor: businessFunction.colorValue,
                    extendedData:    {
                        ...commonDatasetExtendedProps,
                        businessFunctionId: businessFunctionId,
                        businessFunction:   businessFunction,
                    }
                };
            } );

            projectChartData.datasets = _.values( businessFunctionDatasets );

            const hasProjectData = projectChartData.datasets.length;

            // Compact data?
            if( compileBusinessFunctions && hasProjectData ){

                projectChartData.datasets = [
                    {
                        ...commonDatasetProps,
                        label:           project.name,
                        data:            compiledYearlyCountTotals,
                        backgroundColor: dataSetColors[ projectIndex ],
                        extendedData:    {
                            ...commonDatasetExtendedProps,
                            businessFunction:          null,
                            businessFunctionBreakdown: businessFunctionDatasets,
                        }
                    }
                ];

            }

            // Single Project Data
            if( hasProjectData ){

                projectDataSets.push( projectChartData );
            }

        } );

        // All Project Data
        return {
            labels:   fullYearSet,
            datasets: _.chain( projectDataSets ).pluck( 'datasets' ).flatten().value(),
        };
    }
}


